import Component from '../../js/_Component';
import Modal from '../../js/util/_Modal';

const CNAME = 'c-video-player-with-list';

export default class VideoPlayerWithList extends Component {
	private urlPattern: RegExp;

	private baseUrl: string;

	private queryString: string;

	private iframe: HTMLIFrameElement;

	private thumbs: NodeListOf<HTMLElement>;

	private activeThumbClass: string;

	private activeVideoIndex: number;

	constructor(element: HTMLElement) {
		super(element);

		this.urlPattern = /^(?:https?:\/\/|\/\/)?(?:[a-z]*\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})(?![\w-])/;
		this.baseUrl = 'https://www.youtube.com/embed/';
		this.queryString = '?controls=1&rel=0&showinfo=0&autohide=1';
		this.iframe = element.querySelector(`.${CNAME}__iframe`);
		this.thumbs = element.querySelectorAll(`.${CNAME}__thumb`);
		this.activeThumbClass = `${CNAME}__thumb--active`;
		this.activeVideoIndex = null;

		this.thumbs.forEach((thumb, index) => {
			thumb.addEventListener('click', () => this.selectVideo(index));

			thumb.addEventListener('keydown', (event) => {
				if (event.key === 'Space' || event.key === 'Enter') {
					event.preventDefault();
					this.selectVideo(index);
				}
			});

			this.addFallbackThumbnail(thumb);
		});

		this.selectVideo(0, true);
	}

	addFallbackThumbnail(thumb) {
		// Thumbnail div that has image background.
		const thumbImageDiv = thumb.querySelector(`.${CNAME}__thumb-image`) as HTMLElement;

		// Get background image url.
		const thumbBackgroundImage = thumbImageDiv.style.backgroundImage;

		// Verify YT video, matching pattern.
		const match = this.urlPattern.exec(thumb.dataset.url);

		// Set the ID of the video.
		const videoId = match ? match[1] : 'not-found';

		// Get the YT thumbnail URL with the video ID.
		const youTubeThumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

		// Add the fallback image. Exploits that we can have multiple images in a single background.
		thumbImageDiv.style.backgroundImage = `${thumbBackgroundImage}, url('${youTubeThumb}')`;
	}

	/**
	 * Selects the video to display in the player.
	 * @param {Number} index The index of the video.
	 */
	selectVideo(index, force = false) {
		if (!this.thumbs.length) {
			return;
		}

		if (!force && !this.element.classList.contains('min-48rem')) { // Too small, open in modal.
			const thumb = this.thumbs[index];
			const match = this.urlPattern.exec(thumb.dataset.url);
			const videoId = match ? match[1] : null;
			const container = document.createElement('div');
			container.style.width = '100%';
			container.style.paddingTop = '56.25%';
			container.style.position = 'relative';
			const iframe = document.createElement('iframe');
			iframe.style.position = 'absolute';
			iframe.style.left = '0';
			iframe.style.top = '0';
			iframe.style.width = '100%';
			iframe.style.height = '100%';
			iframe.allowFullscreen = true;
			iframe.frameBorder = '0';
			iframe.src = videoId ? `${this.baseUrl}${videoId}${this.queryString}&autoplay=1` : '';
			container.appendChild(iframe);
			new Modal(container);
			return;
		}

		if (index === this.activeVideoIndex) {
			return;
		}

		this.updateActiveVideo(index);
		this.updateActiveThumb(index);
		this.activeVideoIndex = index;
	}

	/**
	 * Sets the iframe attributes with the new video data.
	 * @param {Number} index The index of the video.
	 */
	updateActiveVideo(index) {
		const thumb = this.thumbs[index];
		const match = this.urlPattern.exec(thumb.dataset.url);
		const videoId = match ? match[1] : null;
		const autoplay = this.activeVideoIndex === null ? '' : '&autoplay=1';
		this.iframe.src = videoId ? `${this.baseUrl}${videoId}${this.queryString}${autoplay}` : '';
		this.iframe.title = thumb.dataset.title || '';
		this.scrollToPlayer();
	}

	/**
	 * Adds the active thumbnail class to the new index, and removes it from the old index.
	 * @param {Number} index The index of the video.
	 */
	updateActiveThumb(index) {
		if (this.activeVideoIndex !== null) {
			this.thumbs[this.activeVideoIndex].classList.remove(this.activeThumbClass);
		}

		this.thumbs[index].classList.add(this.activeThumbClass);
	}

	/**
	 * Scrolls the window to the video player.
	 */
	scrollToPlayer() {
		const yPosition = window.pageYOffset + this.iframe.getBoundingClientRect().top;
		const padding = 24;
		window.scrollTo({top: yPosition - padding, behavior: 'smooth'});
	}
}

Component.register(VideoPlayerWithList, 'VideoPlayerWithList');
